/**
 * Odhad vzdáleností/jízdních dob trasy z přímé vzdálenosti (Anatomie plánovače tras §6.4).
 * Konstanty musí zůstat **shodné s BE** (`StationChainEstimate.cs`): haversine × 1,25,
 * rychlost ~65 km/h, zaokrouhlení na 5 min — měň obojí naráz.
 */

export interface GeoPoint {
  lat: number;
  lng: number;
}

const ROAD_FACTOR = 1.25;
const SPEED_KMH = 65;
const ROUND_MINUTES = 5;
const EARTH_RADIUS_KM = 6371;

function toRadians(degrees: number): number {
  return (degrees * Math.PI) / 180;
}

/** Vzdálenost po velké kružnici (km) mezi dvěma WGS-84 body. */
export function haversineKm(a: GeoPoint, b: GeoPoint): number {
  const dLat = toRadians(b.lat - a.lat);
  const dLng = toRadians(b.lng - a.lng);
  const h =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(a.lat)) *
      Math.cos(toRadians(b.lat)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  return EARTH_RADIUS_KM * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** Odhad délky úseku v km = haversine × silniční koeficient. */
export function roadDistanceKm(a: GeoPoint, b: GeoPoint): number {
  return haversineKm(a, b) * ROAD_FACTOR;
}

/** Odhad přejezdu (min) z délky úseku — zaokrouhleno na násobek 5 min. */
export function estimateTravelMinutes(distanceKm: number): number {
  const minutes = (distanceKm / SPEED_KMH) * 60;
  return Math.round(minutes / ROUND_MINUTES) * ROUND_MINUTES;
}

/** Odhad přejezdu mezi dvěma body (min). */
export function estimateTravelBetween(a: GeoPoint, b: GeoPoint): number {
  return estimateTravelMinutes(roadDistanceKm(a, b));
}
